/**
 * @file site-access-errors.ts
 * @description Тексты ошибок режима «Сайт закрыт» (phase19.16, SB138;
 * db-design-site-closed.md §3 «Коды ошибок», uiux-site-closed.md §8).
 * Код сервера → фраза для человека — реестром (OCP): новый код = строка.
 *
 * Конфликт «уже закрыт» (решение оркестратора §7.25, вопрос 7): сервер
 * присылает готовый текст «Сайт уже закрыт с 14:18 (Мария).» в `message` —
 * он показывается как есть; запасной вариант собирается из `details`. Нет связи — своя фраза у
 * каждого действия: она говорит, в каком состоянии сайт остался.
 */

import { ApiError } from "../api-client";
import { zonedClock } from "./site-access-time";

/** Действие, ради которого шёл запрос: от него зависит фраза «нет связи». */
export type SiteAccessAction = "close" | "open" | "extend" | "staffPass" | "load";

/** «Нет связи» — что случилось с сайтом (uiux §8). */
export const SITE_ACCESS_NETWORK_TEXT: Readonly<Record<SiteAccessAction, string>> = {
  close: "Не удалось закрыть сайт — нет связи с сервером. Сайт по-прежнему открыт.",
  open: "Не удалось открыть сайт — нет связи. Сайт пока закрыт.",
  extend: "Не удалось продлить паузу — нет связи. Сайт откроется в прежний срок.",
  staffPass: "Не удалось открыть сайт для просмотра — нет связи с сервером.",
  load: "Не удалось узнать, открыт ли сайт.",
};

/** Код ошибки сервера → текст. */
export const SITE_ACCESS_ERROR_TEXT: Readonly<Record<string, string>> = {
  site_closed_by_platform: "Сайт закрыт платформой Commora — закрыть, открыть или посмотреть его сейчас может только платформа.",
  site_not_closed: "Сайт уже открыт — смотрите его как обычно.",
  site_already_open: "Срок паузы уже прошёл — сайт открыт для покупателей.",
  site_closure_no_deadline: "У паузы нет срока — продлевать нечего. Сайт откроется, когда вы его откроете.",
  site_closure_not_found: "Эта пауза уже закончилась — показываем, что с сайтом сейчас.",
  site_closure_text_too_long: "Текст для покупателей длиннее 200 знаков — сократите его.",
  site_closure_text_discount: "В тексте для покупателей нельзя указывать размер скидки — уберите цифры скидки.",
  site_closure_expected_invalid: "«Откроем примерно в» — время позже текущего и не дальше чем через 7 дней.",
  rbac_forbidden: "Закрывать и открывать сайт может владелец или сотрудник с правом настроек магазина.",
  site_preview_not_configured: "Просмотр закрытого сайта не настроен на сервере — напишите в поддержку платформы.",
};

/** Коды, после которых состояние на экране устарело — перечитать. */
export const SITE_ACCESS_STALE_CODES: ReadonlySet<string> = new Set([
  "site_already_closed",
  "site_closed_by_platform",
  "site_not_closed",
  "site_already_open",
  "site_closure_no_deadline",
  "site_closure_not_found",
]);

/** Подробности конфликта «уже закрыт». */
interface AlreadyClosedDetails {
  closedAt: string;
  closedBy: { name: string } | null;
}

function alreadyClosedDetails(details: unknown): AlreadyClosedDetails | null {
  if (typeof details !== "object" || details === null) return null;
  const { closedAt, closedBy } = details as { closedAt?: unknown; closedBy?: unknown };
  if (typeof closedAt !== "string" || Number.isNaN(Date.parse(closedAt))) return null;
  const name = typeof closedBy === "object" && closedBy !== null ? (closedBy as { name?: unknown }).name : null;
  return { closedAt, closedBy: typeof name === "string" && name ? { name } : null };
}

/** «Сайт уже закрыт с 14:18 (Мария)». */
export function alreadyClosedText(details: unknown, zone: string): string {
  const d = alreadyClosedDetails(details);
  if (!d) return "Сайт уже закрыт.";
  const time = zonedClock(new Date(d.closedAt), zone);
  return d.closedBy ? `Сайт уже закрыт с ${time} (${d.closedBy.name}).` : `Сайт уже закрыт с ${time}.`;
}

/** Текст ошибки действия для человека. */
export function siteAccessErrorText(err: unknown, action: SiteAccessAction, zone: string): string {
  if (!(err instanceof ApiError)) return SITE_ACCESS_NETWORK_TEXT[action];
  if (err.code === "network_error") return SITE_ACCESS_NETWORK_TEXT[action];
  // Сервер уже прислал готовую фразу «Сайт уже закрыт с 14:18 (Мария).» — показываем как есть;
  // собираем её из details, только если текста нет.
  if (err.code === "site_already_closed") return err.message.trim() || alreadyClosedText(err.details, zone);
  return SITE_ACCESS_ERROR_TEXT[err.code] ?? (err.message || SITE_ACCESS_NETWORK_TEXT[action]);
}

/** Нет связи — окно остаётся открытым с «Повторить». */
export function isSiteAccessNetworkError(err: unknown): boolean {
  return !(err instanceof ApiError) || err.code === "network_error";
}

/** Ошибка говорит, что состояние на экране устарело. */
export function isSiteAccessStale(err: unknown): boolean {
  return err instanceof ApiError && SITE_ACCESS_STALE_CODES.has(err.code);
}
